/* ===== Accept Invitation Page (public) ===== */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Invitation } from '@zetyar/domain'
import { Lock, LogIn, User } from 'lucide-react'
import { Card } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { getSupabaseClient } from '@/infrastructure/supabase/app'
import { webInvitationRepository } from '@/infrastructure/repositories/invitations'

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(true)
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pendingConfirmation, setPendingConfirmation] = useState(false)

  useEffect(() => {
    let isMounted = true

    const loadInvitation = async () => {
      if (!token) {
        setIsLoadingInvitation(false)
        return
      }

      try {
        const result = await webInvitationRepository.getByToken(token)
        if (isMounted) {
          setInvitation(result)
        }
      } catch (error) {
        console.error('Error al cargar la invitacion:', error)
        if (isMounted) {
          setInvitation(null)
        }
      } finally {
        if (isMounted) {
          setIsLoadingInvitation(false)
        }
      }
    }

    void loadInvitation()

    return () => {
      isMounted = false
    }
  }, [token])

  const isExpired = invitation ? new Date(invitation.expiresAt).getTime() < Date.now() : false
  const isUsable = invitation && invitation.status === 'pending' && !isExpired

  const completeMembership = async (authUid: string, name: string) => {
    await webInvitationRepository.accept({ token, authUid, displayName: name })

    // Membership was just created after AppContextProvider's onAuthStateChange already
    // fired and loaded (empty) organizations for this account. A full reload re-runs that
    // load with the membership now in place, instead of leaving the dashboard stuck on a
    // stale empty organization list.
    window.location.href = window.location.pathname + '#/dashboard'
    window.location.reload()
  }

  // Covers the "Confirm email" flow: signUp below has no session to act on yet, so it just
  // asks the user to check their inbox. Supabase's own confirmation email (emailRedirectTo)
  // sends them back to this exact URL once confirmed - at that point there IS a session, but
  // handleAccept already returned and nothing else would ever call completeMembership. This
  // effect picks that up: if we land here already authenticated as the invited email, finish
  // the membership automatically instead of asking them to sign up a second time.
  useEffect(() => {
    if (!invitation || !isUsable) return
    let cancelled = false

    const resumeIfAlreadyConfirmed = async () => {
      const client = getSupabaseClient()
      const { data } = await client.auth.getUser()
      const user = data.user
      if (!user || user.email !== invitation.email || cancelled) return

      setIsSubmitting(true)
      try {
        const name = (user.user_metadata?.display_name as string | undefined) || user.email || ''
        await completeMembership(user.id, name)
      } catch (error) {
        console.error('Error al completar la invitacion tras confirmar el correo:', error)
        if (!cancelled) {
          setErrorMessage(
            'Tu correo ya esta confirmado pero no pudimos completar tu membresia. Pide a un administrador que revise la invitacion.',
          )
        }
      } finally {
        if (!cancelled) setIsSubmitting(false)
      }
    }

    void resumeIfAlreadyConfirmed()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitation, isUsable])

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!invitation) return

    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      const client = getSupabaseClient()
      const { data, error } = await client.auth.signUp({
        email: invitation.email,
        password,
        options: { data: { display_name: displayName }, emailRedirectTo: window.location.href },
      })
      if (error) throw error

      if (!data.session || !data.user) {
        // Email confirmation is required before a session exists; the effect above finishes
        // the job once the user comes back from Supabase's confirmation email.
        setPendingConfirmation(true)
        return
      }

      await completeMembership(data.user.id, displayName)
    } catch (error) {
      console.error('Error al aceptar la invitacion:', error)
      setErrorMessage(
        'No pudimos crear tu cuenta. Si ya tienes una cuenta con este correo, inicia sesion y pide a un administrador que revise la invitacion.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="app-shell flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-modal">
        <div className="text-center mb-6">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[14px] bg-gradient-to-br from-primary-500 to-[#8e67ff] text-2xl font-bold text-white shadow-glow">
            Z
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 ">
            Zetyar Nexo
          </h1>
        </div>

        {isLoadingInvitation ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-6">
            Verificando invitacion...
          </p>
        ) : !invitation ? (
          <p className="text-sm text-danger-600 text-center py-6">
            Esta invitacion no existe o el enlace no es valido.
          </p>
        ) : !isUsable ? (
          <p className="text-sm text-danger-600 text-center py-6">
            Esta invitacion ya fue usada, revocada o expiro.
          </p>
        ) : pendingConfirmation ? (
          <p className="text-sm text-neutral-600 dark:text-neutral-300 text-center py-6">
            Te enviamos un correo a <strong>{invitation.email}</strong> para confirmar tu cuenta.
            Una vez confirmada, vuelve a este enlace para terminar de unirte a{' '}
            <strong>{invitation.organization?.name}</strong>.
          </p>
        ) : (
          <>
            <p className="text-sm text-neutral-600 dark:text-neutral-300 text-center mb-6">
              Te invitaron a unirte a <strong>{invitation.organization?.name}</strong> con el
              correo <strong>{invitation.email}</strong>.
            </p>

            {errorMessage && (
              <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-900/70 dark:bg-danger-950/40 dark:text-danger-200">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleAccept} className="space-y-4">
              <Input
                label="Nombre completo"
                placeholder="Tu nombre"
                leftIcon={<User className="w-4 h-4 text-neutral-400" />}
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <Input
                label="Crea una contrasena"
                type="password"
                placeholder="Minimo 6 caracteres"
                leftIcon={<Lock className="w-4 h-4 text-neutral-400" />}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <Button
                fullWidth
                variant="primary"
                size="lg"
                type="submit"
                leftIcon={<LogIn className="w-5 h-5" />}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creando cuenta...' : 'Crear cuenta y unirme'}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  )
}
